/**
 * Column priority configurations for responsive table variants.
 *
 * Priority levels:
 *   1 - Critical: always visible, maps to card header/title
 *   2 - Important: always visible, shows in card body grid
 *   3 - Supplementary: visible in card body
 *   4 - Low: hidden on mobile, desktop only
 *   'action' - Interactive: transformed into card tap target
 *
 * mobileRole values:
 *   'title'      - card title row
 *   'subtitle'   - card subtitle (date/time)
 *   'badge'      - inline badge in header or body
 *   'field'      - key-value pair in card body grid
 *   'expandable' - long text, truncated with "more" toggle
 *   'footer'     - card footer row (scores, status)
 *   'tap'        - action column, becomes the card tap target
 *   null         - hidden on mobile
 */

export const callResultsPriority = {
  details: { priority: 'action', mobileRole: 'tap' },
  call_id: { priority: 4, mobileRole: null },
  call_date: { priority: 1, mobileRole: 'subtitle' },
  campaign_name: { priority: 1, mobileRole: 'title' },
  direction: { priority: 2, mobileRole: 'field' },
  phone_number: { priority: 2, mobileRole: 'field' },
  caller_name: { priority: 2, mobileRole: 'field' },
  caller_company: { priority: 4, mobileRole: null },
  disposition: { priority: 2, mobileRole: 'badge' },
  agent_name: { priority: 2, mobileRole: 'field' },
  caller_email: { priority: 4, mobileRole: null },
  ring_time: { priority: 4, mobileRole: null },
  talk_time: { priority: 4, mobileRole: null },
  handle_time: { priority: 4, mobileRole: null },
  call_notes: { priority: 2, mobileRole: 'expandable' },
  feedback: { priority: 3, mobileRole: 'footer' }
}

export const campaignPriority = {
  campaign_name: { priority: 1, mobileRole: 'title' },
  call_count: { priority: 1, mobileRole: 'field' },
  call_minutes: { priority: 2, mobileRole: 'field' },
  plan_minutes: { priority: 2, mobileRole: 'field' },
  plan_remaining_percent: { priority: 1, mobileRole: 'badge' },
  actions: { priority: 'action', mobileRole: 'tap' }
}

export const qaSummaryPriority = {
  details: { priority: 'action', mobileRole: 'tap' },
  evaluatedAt: { priority: 1, mobileRole: 'subtitle' },
  callId: { priority: 4, mobileRole: null },
  campaignName: { priority: 1, mobileRole: 'title' },
  agentName: { priority: 2, mobileRole: 'field' },
  disposition: { priority: 3, mobileRole: 'field' },
  qaGrade: { priority: 1, mobileRole: 'badge' },
  overallScore: { priority: 1, mobileRole: 'footer' },
  failed_criteria: { priority: 2, mobileRole: 'expandable' }
}

export const usageOverviewPriority = {
  campaign_name: { priority: 1, mobileRole: 'title' },
  plan_used_percent: { priority: 1, mobileRole: 'footer' },
  current_month_minutes: { priority: 2, mobileRole: 'field' },
  plan_minutes: { priority: 2, mobileRole: 'field' },
  call_count: { priority: 2, mobileRole: 'field' },
  last_call: { priority: 3, mobileRole: 'field' },
  status: { priority: 1, mobileRole: 'badge' }
}

export const availabilityPriority = {
  campaign_name: { priority: 1, mobileRole: 'title' },
  user_name: { priority: 1, mobileRole: 'subtitle' },
  isAvailable: { priority: 1, mobileRole: 'badge' }
}

export const callLogPriority = {
  details: { priority: 'action', mobileRole: 'tap' },
  timestamp: { priority: 1, mobileRole: 'subtitle' },
  direction: { priority: 1, mobileRole: 'badge' },
  from: { priority: 2, mobileRole: 'field' },
  to: { priority: 2, mobileRole: 'field' },
  customerName: { priority: 1, mobileRole: 'title' },
  customerCompany: { priority: 3, mobileRole: 'field' },
  agentName: { priority: 2, mobileRole: 'field' },
  email: { priority: 4, mobileRole: null },
  callType: { priority: 2, mobileRole: 'badge' },
  handleTime: { priority: 3, mobileRole: 'field' },
  callNote: { priority: 2, mobileRole: 'expandable' },
  rating: { priority: 3, mobileRole: 'footer' }
}

/**
 * Get the priority config for a given table variant.
 * @param {string} variant - 'calls' | 'campaigns' | 'qa' | 'callLog'
 * @returns {Object} priority map
 */
export const getPriorityConfig = (variant) => {
  switch (variant) {
    case 'calls': return callResultsPriority
    case 'campaigns': return campaignPriority
    case 'qa': return qaSummaryPriority
    case 'callLog': return callLogPriority
    case 'usageOverview': return usageOverviewPriority
    case 'availability': return availabilityPriority
    default: return {}
  }
}
